import type { CatalogueEntry } from "./types";

import absAsgsMainStructure from "./entries/abs-asgs-2026-main-structure";
import absBoundaries from "./entries/abs-boundaries";
import electoralBoundaries from "./entries/electoral-boundaries";
import gNaf from "./entries/g-naf";
import gNafCore from "./entries/g-naf-core";
import landUseLandCover from "./entries/land-use-land-cover";
import nationalAirports from "./entries/national-airports";
import nationalBuildings from "./entries/national-buildings";
import nationalCadastre from "./entries/national-cadastre";
import nationalLocalGovernmentAreas from "./entries/national-local-government-areas";
import nationalLocalities from "./entries/national-localities";
import nationalPlanning from "./entries/national-planning";
import nationalPlanningInsights from "./entries/national-planning-insights";
import nationalProperty from "./entries/national-property";
import nationalRail from "./entries/national-rail";
import nationalRoads from "./entries/national-roads";
import nationalSolar from "./entries/national-solar";
import nationalSurfaceCover from "./entries/national-surface-cover";
import nationalTrees from "./entries/national-trees";
import nationalWards from "./entries/national-wards";
import postcodeBoundaries from "./entries/postcode-boundaries";
import stateBoundaries from "./entries/state-boundaries";
import townPoints from "./entries/town-points";

// `readonly` so one caller's in-place sort can't reorder the shared list.
export const catalogueEntries: readonly CatalogueEntry[] = [
  absAsgsMainStructure,
  absBoundaries,
  electoralBoundaries,
  gNaf,
  gNafCore,
  landUseLandCover,
  nationalAirports,
  nationalBuildings,
  nationalCadastre,
  nationalLocalGovernmentAreas,
  nationalLocalities,
  nationalPlanning,
  nationalPlanningInsights,
  nationalProperty,
  nationalRail,
  nationalRoads,
  nationalSolar,
  nationalSurfaceCover,
  nationalTrees,
  nationalWards,
  postcodeBoundaries,
  stateBoundaries,
  townPoints,
].sort((a, b) => a.displayName.localeCompare(b.displayName));

// What the catalogue list and search see. Hidden entries stay registered so
// their slug resolves and their entry page renders.
const visibleEntries = catalogueEntries.filter((e) => !e.hidden);

const bySlug = new Map(catalogueEntries.map((e) => [e.slug, e]));

export const getCatalogueEntry = (slug: string): CatalogueEntry | undefined =>
  bySlug.get(slug);

// Aliases for ids that uppercasing the display name can't recover.
const LEGACY_ID_TO_SLUG: Record<string, string> = {
  "NATIONAL ELECTORAL BOUNDARIES": "electoral-boundaries",
  "NATIONAL POSTCODE BOUNDARIES": "postcode-boundaries",
};

// Deal with legacy URI, redirect if people use them.
export const LEGACY_SLUG_REDIRECTS: Record<string, string> =
  catalogueEntries.reduce<Record<string, string>>(
    (acc, entry) => {
      acc[entry.datasetProductCode] = entry.slug;
      acc[entry.displayName.toUpperCase()] = entry.slug;
      return acc;
    },
    { ...LEGACY_ID_TO_SLUG },
  );

// Resolves a URL segment to a canonical slug, accepting legacy forms.
export const resolveCatalogueSlug = (segment: string): string | undefined => {
  if (bySlug.has(segment)) return segment;
  let decoded: string;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    decoded = segment;
  }

  return LEGACY_SLUG_REDIRECTS[decoded.toUpperCase()];
};

export const searchCatalogue = (query: string): CatalogueEntry[] => {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return [...visibleEntries];

  return visibleEntries.filter((entry) =>
    `${entry.displayName} ${entry.searchTags} ${entry.shortDescription}`
      .toLowerCase()
      .includes(trimmed),
  );
};

export type {
  CatalogueEntry,
  CatalogueCardEntry,
  CatalogueDetail,
  DataDictionaryTable,
  DictionaryField,
  DictionaryCode,
} from "./types";

// Catches an entry that was imported but never added to `catalogueEntries`.
const EXPECTED_ENTRY_COUNT = 23;

if (import.meta.env.DEV) {
  if (catalogueEntries.length !== EXPECTED_ENTRY_COUNT) {
    throw new Error(
      `Expected ${EXPECTED_ENTRY_COUNT} catalogue entries but found ` +
        `${catalogueEntries.length}. Adding or removing a dataset means ` +
        `updating its import, the \`catalogueEntries\` array and ` +
        `\`EXPECTED_ENTRY_COUNT\`.`,
    );
  }

  const slugs = catalogueEntries.map((e) => e.slug);
  const duplicateSlugs = slugs.filter((s, i) => slugs.indexOf(s) !== i);
  if (duplicateSlugs.length) {
    throw new Error(
      `Duplicate catalogue slugs: ${[...new Set(duplicateSlugs)].join(", ")}`,
    );
  }

  const codes = catalogueEntries.map((e) => e.datasetProductCode);
  const duplicateCodes = codes.filter((c, i) => codes.indexOf(c) !== i);
  if (duplicateCodes.length) {
    throw new Error(
      `Duplicate datasetProductCodes: ${[...new Set(duplicateCodes)].join(", ")}`,
    );
  }

  for (const entry of catalogueEntries) {
    if (!/^[a-z0-9-]+$/.test(entry.slug)) {
      throw new Error(`Catalogue slug is not URL-safe: "${entry.slug}"`);
    }
    if (!entry.imageLight || !entry.imageDark) {
      throw new Error(`Catalogue entry "${entry.slug}" is missing an image`);
    }
    if (!entry.metadata.dataType.length) {
      throw new Error(`Catalogue entry "${entry.slug}" has no dataType`);
    }
  }

  for (const [legacyId, slug] of Object.entries(LEGACY_ID_TO_SLUG)) {
    if (!bySlug.has(slug)) {
      throw new Error(
        `Legacy catalogue id "${legacyId}" redirects to "${slug}", ` +
          `which is not a catalogue entry.`,
      );
    }
  }
}
